// File: frontend/src/components/entries/EntryEditorToolbar.tsx
import React, { useState, useRef, useEffect } from "react";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { MatchModel } from "../../models";
import { ClubModel } from "../../models/ClubModel";
import { TeamModel, getTeamDisplayName, getTeamUrlSlug } from "../../models/TeamModel";
import { searchMatches } from "../../services/matchService";
import { uploadEntryImage } from "../../services/profileService";
import clubService from "../../services/clubService";
import teamService from "../../services/teamService";
import { SEARCH_DEBOUNCE_MS, SEARCH_MAX_RESULTS } from "../../constants";
import { searchCategory } from "../../services/searchService";
import { SearchCustomTopicResult, SearchPersonResult } from "../../models/SearchModel";
import { toLowerTr, stripDisambiguationSuffix } from "../../utils/entryContentUtils";
import { FaFutbol, FaUser, FaBuilding, FaShieldAlt, FaHashtag } from "react-icons/fa";
import { TokenPromptModal } from "./TokenPromptModal";
import { InputClearButton } from "../common/InputClearButton";
import { useAuth } from "../../context/AuthContext";

import { UnifiedRefPopup } from "./UnifiedRefPopup";
// ─── Ref insert mode ──────────────────────────────────────────────────────────

/**
 * How a selected reference is inserted into the entry content:
 *  - "inline": the label itself becomes the link, flowing inside the sentence
 *  - "bkz":    the reference is rendered as a parenthesized "(bkz: label)" note
 * The "bkz" variant is encoded by prefixing the token keyword with "bkz-"
 * (e.g. (bkz-mac: label||id)); renderers unwrap it back to "(bkz: <link>)".
 */
export type RefInsertMode = "inline" | "bkz";

/** Prefixes the token keyword when the bkz insert mode is active. */
export const applyRefMode = (keyword: string, mode: RefInsertMode): string =>
  mode === "bkz" ? `bkz-${keyword}` : keyword;

/**
 * Segmented two-option switch shown at the top of every ref popup.
 * Lets the user pick between the inline link and the "(bkz: ...)" note
 * before selecting a search result.
 */
export const RefModeToggle: React.FC<{ mode: RefInsertMode; onChange: (m: RefInsertMode) => void }> = ({ mode, onChange }) => {
  const { t } = useTranslation();
  return (
    <div className="ref-popup-mode">
      <button
        type="button"
        className={`ref-popup-mode-btn${mode === "inline" ? " ref-popup-mode-btn--active" : ""}`}
        onClick={() => onChange("inline")}
      >
        {t("ref_mode_inline")}
      </button>
      <button
        type="button"
        className={`ref-popup-mode-btn${mode === "bkz" ? " ref-popup-mode-btn--active" : ""}`}
        onClick={() => onChange("bkz")}
      >
        {t("ref_mode_bkz")}
      </button>
    </div>
  );
};

// ─── RefSearchPopup ───────────────────────────────────────────────────────────

interface RefSearchPopupProps {
  onSelect: (text: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
  /**
   * Text selected in the editor when the button was pressed, used to pre-fill
   * the search box — the same behaviour the person/club/team/topic popups
   * already had. The match popup was the only one opening empty, so selecting
   * a team name and reaching for a match reference meant retyping it.
   */
  initialQuery?: string;
}

/**
 * Popup for selecting a match reference to insert as (mac: label||id).
 * Extracted from EntryPostForm so it can be reused in edit mode (EntryRow).
 */
// Build a year list from the current year down to 2020 for the year filter dropdown
const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from(
  { length: CURRENT_YEAR - 2019 },
  (_, i) => CURRENT_YEAR - i
);

export const RefSearchPopup: React.FC<RefSearchPopupProps> = ({ onSelect, onClose, style, initialQuery = "" }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [selectedYear, setSelectedYear] = useState<string>("");
  const [results, setResults] = useState<MatchModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Insert mode: inline link vs parenthesized "(bkz: ...)" note
  const [refMode, setRefMode] = useState<RefInsertMode>("inline");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus the search input when the popup opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close popup on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  /**
   * Normalize a raw search query before sending to the backend.
   * - Strip hyphens and dashes so "fenerbahce - agf" becomes "fenerbahce agf"
   * - Collapse multiple whitespace characters to a single space and trim.
   */
  const normalizeQuery = (raw: string): string =>
    raw.trim().replace(/[-\u2013\u2014]+/g, " ").replace(/\s+/g, " ").trim();

  /**
   * Build the final query string to send to the backend.
   * When a year is selected it is appended so the backend date parser picks it up
   * and narrows results to that calendar year (e.g. "fenerbahce 2026").
   */
  const buildSearchQuery = (raw: string, year: string): string => {
    const base = normalizeQuery(raw);
    return year ? `${base} ${year}`.trim() : base;
  };

  // Debounced search — fires when query or year filter changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const finalQuery = buildSearchQuery(query, selectedYear);
    if (finalQuery.length < 1) {
      setResults([]);
      setError("");
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError("");
      try {
        const matches = await searchMatches(finalQuery, SEARCH_MAX_RESULTS);
        setResults(matches);
      } catch {
        setError(t("search_error"));
      } finally {
        setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, selectedYear]);

  const handleSelect = (match: MatchModel) => {
    const date =
      match.matchDate instanceof Date ? match.matchDate : new Date(match.matchDate);

    // Turkish month names with proper Turkish characters
    const turkishMonths = [
      "Ocak", "Şubat", "Mart", "Nisan", "Mayıs", "Haziran",
      "Temmuz", "Ağustos", "Eylül", "Ekim", "Kasım", "Aralık",
    ];
    const day = date.getDate();
    const month = turkishMonths[date.getMonth()];
    const year = date.getFullYear();

    // "14 Mart 2026 Fenerbahçe Galatasaray maçı" (date first)
    const label = `${day} ${month} ${year} ${match.homeTeam} ${match.awayTeam} maçı`;

    // Format: (mac: label||matchId) — matchId after || is used by EntryRow for navigation
    // Any trailing "(...)" disambiguation suffix in the team names is stripped
    // from the inline label (see stripDisambiguationSuffix); label is then
    // lowercased (sözlük convention: entry content is all lowercase);
    // the matchId tail is left untouched so navigation keeps working.
    onSelect(`(${applyRefMode("mac", refMode)}: ${toLowerTr(stripDisambiguationSuffix(label))}||${match.id})`);
    onClose();
  };

  const formatMatchLabel = (match: MatchModel): string => {
    const date =
      match.matchDate instanceof Date ? match.matchDate : new Date(match.matchDate);
    const dateStr = date.toLocaleDateString("tr-TR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
    return `${match.homeTeam} - ${match.awayTeam}  (${dateStr})`;
  };

  const hasInput = normalizeQuery(query).length >= 1 || selectedYear !== "";

  return (
    <div className="ref-popup" style={style}>
      <div className="ref-popup-header">
        <span className="ref-popup-title">{t("ref_search_title")}</span>
        <button type="button" className="ref-popup-close" onClick={onClose} title={t("close")}>✕</button>
      </div>
      {/* Insert mode switch — inline link vs "(bkz: ...)" note */}
      <RefModeToggle mode={refMode} onChange={setRefMode} />
      {/* Search row: text input + year filter dropdown side by side */}
      <div className="ref-popup-search-row">
        <input
          ref={inputRef}
          type="text"
          className="ref-popup-input"
          placeholder={t("ref_search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query !== "" && (
          <InputClearButton variant="inline" onClear={() => setQuery("")} />
        )}
        <select
          className="ref-popup-year-select"
          value={selectedYear}
          onChange={(e) => setSelectedYear(e.target.value)}
          title={t("ref_search_year_filter", "Yıla göre filtrele")}
        >
          <option value="">{t("ref_search_all_years", "Tüm yıllar")}</option>
          {YEAR_OPTIONS.map((year) => (
            <option key={year} value={String(year)}>{year}</option>
          ))}
        </select>
      </div>
      <div className="ref-popup-results">
        {isLoading && <div className="ref-popup-status">{t("searching")}</div>}
        {!isLoading && error && (
          <div className="ref-popup-status ref-popup-status--error">{error}</div>
        )}
        {!isLoading && !error && hasInput && results.length === 0 && (
          <div className="ref-popup-status">{t("no_results")}</div>
        )}
        {!isLoading && results.map((match) => (
          <button
            key={match.id}
            type="button"
            className="ref-popup-result-item"
            onClick={() => handleSelect(match)}
          >
            {formatMatchLabel(match)}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── PersonRefSearchPopup ─────────────────────────────────────────────────────

interface PersonRefSearchPopupProps {
  onSelect: (text: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
  /** Pre-fills the search input with the text selected in the textarea when the button was clicked */
  initialQuery?: string;
}

/**
 * Popup for selecting a person reference to insert as (kisi: displayName||slug).
 * Uses the "people" category of the general search endpoint (searchCategory),
 * which ranks results by relevance to the query (exact match first) and
 * supports multi-word queries in any order — unlike the left-pane people
 * listing endpoint this popup used previously, which only sorted by recent
 * activity and had no notion of "best match" at all.
 */
export const PersonRefSearchPopup: React.FC<PersonRefSearchPopupProps> = ({ onSelect, onClose, style, initialQuery = "" }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchPersonResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Insert mode: inline link vs parenthesized "(bkz: ...)" note
  const [refMode, setRefMode] = useState<RefInsertMode>("inline");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus the search input when the popup opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close popup on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Debounced search — fires when query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError("");
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError("");
      try {
        const res = await searchCategory(trimmed, "people", 0, SEARCH_MAX_RESULTS);
        setResults(res.people?.content ?? []);
      } catch {
        setError(t("search_error"));
      } finally {
        setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleSelect = (person: SearchPersonResult) => {
    // Prefer knownName for display (e.g. "Ronaldo"), fall back to full name
    const displayName = person.knownName || `${person.firstName} ${person.lastName}`;
    // Format: (kisi: displayName||id) — the tail is used by EntryRow for
    // navigation. Only the id is stored: the readable label already sits in
    // front of the "||", so repeating the slug would just make the raw token
    // longer for the person writing the entry. The backend resolves the id and
    // redirects to the canonical "/kisi/{slug}--{id}" URL, so the reference
    // survives any later name correction. Same shape match references have
    // always used.
    // Any trailing "(...)" disambiguation suffix (see stripDisambiguationSuffix)
    // is stripped from the inline label; display name is then lowercased
    // (sözlük convention); the slug tail is untouched.
    onSelect(`(${applyRefMode("kisi", refMode)}: ${toLowerTr(stripDisambiguationSuffix(displayName))}||${person.id})`);
    onClose();
  };

  const hasInput = query.trim().length >= 2;

  return (
    <div className="ref-popup" style={style}>
      <div className="ref-popup-header">
        <span className="ref-popup-title">{t("person_ref_search_title")}</span>
        <button type="button" className="ref-popup-close" onClick={onClose} title={t("close")}>✕</button>
      </div>
      {/* Insert mode switch — inline link vs "(bkz: ...)" note */}
      <RefModeToggle mode={refMode} onChange={setRefMode} />
      <div className="ref-popup-search-row">
        <input
          ref={inputRef}
          type="text"
          className="ref-popup-input"
          placeholder={t("person_ref_search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query !== "" && (
          <InputClearButton variant="inline" onClear={() => setQuery("")} />
        )}
      </div>
      <div className="ref-popup-results">
        {isLoading && <div className="ref-popup-status">{t("searching")}</div>}
        {!isLoading && error && (
          <div className="ref-popup-status ref-popup-status--error">{error}</div>
        )}
        {!isLoading && !error && hasInput && results.length === 0 && (
          <div className="ref-popup-status">{t("no_results")}</div>
        )}
        {!isLoading && results.map((person) => {
          const fullName = `${person.firstName} ${person.lastName}`;
          return (
            <button
              key={person.id}
              type="button"
              className="ref-popup-result-item"
              onClick={() => handleSelect(person)}
            >
              {/* Show knownName (if set) as primary, full name as secondary context */}
              {person.knownName
                ? <><strong>{person.knownName}</strong> <span className="ref-popup-result-sub">({fullName})</span></>
                : fullName
              }
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─── ClubRefSearchPopup ───────────────────────────────────────────────────────

interface ClubRefSearchPopupProps {
  onSelect: (text: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
  /** Pre-fills the search input with the text selected in the textarea when the button was clicked */
  initialQuery?: string;
}

/**
 * Popup for selecting a club reference to insert as (kulup: displayName||slug).
 * Inserts the club's display name (nameTr ?? shortName) so the label reads
 * naturally in Turkish entries (e.g. "Fenerbahçe").
 */
export const ClubRefSearchPopup: React.FC<ClubRefSearchPopupProps> = ({ onSelect, onClose, style, initialQuery = "" }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<ClubModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Insert mode: inline link vs parenthesized "(bkz: ...)" note
  const [refMode, setRefMode] = useState<RefInsertMode>("inline");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus the search input when the popup opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close popup on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Debounced search — fires when query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError("");
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError("");
      try {
        const clubs = await clubService.searchClubs(trimmed);
        setResults(clubs.slice(0, SEARCH_MAX_RESULTS));
      } catch {
        setError(t("search_error"));
      } finally {
        setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleSelect = (club: ClubModel) => {
    // Prefer Turkish name, fall back to shortName
    const displayName = club.nameTr ?? club.shortName;
    // Format: (kulup: displayName||id) — tail used by EntryRow to link to
    // /kulup/{tail}. Storing only the id keeps the reference working after a
    // club rename, which regenerates the slug (see AdminClubService).
    // Any trailing "(...)" disambiguation suffix (see stripDisambiguationSuffix)
    // is stripped from the inline label; display name is then lowercased
    // (sözlük convention); the slug tail is untouched.
    onSelect(`(${applyRefMode("kulup", refMode)}: ${toLowerTr(stripDisambiguationSuffix(displayName))}||${club.id})`);
    onClose();
  };

  const hasInput = query.trim().length >= 2;

  return (
    <div className="ref-popup" style={style}>
      <div className="ref-popup-header">
        <span className="ref-popup-title">{t("club_ref_search_title")}</span>
        <button type="button" className="ref-popup-close" onClick={onClose} title={t("close")}>✕</button>
      </div>
      {/* Insert mode switch — inline link vs "(bkz: ...)" note */}
      <RefModeToggle mode={refMode} onChange={setRefMode} />
      <div className="ref-popup-search-row">
        <input
          ref={inputRef}
          type="text"
          className="ref-popup-input"
          placeholder={t("club_ref_search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query !== "" && (
          <InputClearButton variant="inline" onClear={() => setQuery("")} />
        )}
      </div>
      <div className="ref-popup-results">
        {isLoading && <div className="ref-popup-status">{t("searching")}</div>}
        {!isLoading && error && (
          <div className="ref-popup-status ref-popup-status--error">{error}</div>
        )}
        {!isLoading && !error && hasInput && results.length === 0 && (
          <div className="ref-popup-status">{t("no_results")}</div>
        )}
        {!isLoading && results.map((club) => (
          <button
            key={club.id}
            type="button"
            className="ref-popup-result-item"
            onClick={() => handleSelect(club)}
          >
            {/* Primary: Turkish name or shortName; secondary: abbreviation if set */}
            {club.nameTr ?? club.shortName}
            {club.abbreviation && (
              <span className="ref-popup-result-sub"> ({club.abbreviation})</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── TeamRefSearchPopup ───────────────────────────────────────────────────────

interface TeamRefSearchPopupProps {
  onSelect: (text: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
  /** Pre-fills the search input with the text selected in the textarea when the button was clicked */
  initialQuery?: string;
}

/**
 * Popup for selecting a team branch reference to insert as
 * (takim: label||id).
 *
 * The label is built as "{clubName} {sportLabel} takımı" (e.g. "Fenerbahçe futbol takımı",
 * "Beşiktaş basketbol takımı", "Galatasaray voleybol kadın takımı") so the markup
 * reads naturally in Turkish entries.
 */
export const TeamRefSearchPopup: React.FC<TeamRefSearchPopupProps> = ({ onSelect, onClose, style, initialQuery = "" }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<TeamModel[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Insert mode: inline link vs parenthesized "(bkz: ...)" note
  const [refMode, setRefMode] = useState<RefInsertMode>("inline");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus the search input when the popup opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close popup on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Debounced search — fires when query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError("");
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError("");
      try {
        const teams = await teamService.searchTeams(trimmed);
        setResults(teams.slice(0, SEARCH_MAX_RESULTS));
      } catch {
        setError(t("search_error"));
      } finally {
        setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  /**
   * Build the human-readable label for a team ref.
   * Examples:
   *   FOOTBALL / MALE   / null  → "Fenerbahçe futbol takımı"
   *   BASKETBALL / MALE / null  → "Beşiktaş basketbol takımı"
   *   VOLLEYBALL / FEMALE / null → "Galatasaray voleybol kadın takımı"
   *   FOOTBALL / MALE / "u19"  → "Fenerbahçe futbol U19 takımı"
   */
  const buildTeamLabel = (team: TeamModel): string => {
    // Use custom displayName if set (e.g. "Fenerbahçe Beko"), otherwise club short name
    const clubName = getTeamDisplayName(team);
    // Translate sport type and lowercase it (e.g. "Futbol" → "futbol")
    const sportLabel = t(`sport_type_${team.sportType.toLowerCase()}`, team.sportType).toLowerCase();
    // Append gender suffix only for female/mixed teams to keep male labels clean
    const genderSuffix =
      team.gender === "FEMALE" ? " kadın" :
      team.gender === "MIXED" ? " karma" : "";
    // Append teamCode in uppercase when set (e.g. " U19", " 1B")
    const codeSuffix = team.teamCode ? ` ${team.teamCode.toUpperCase()}` : "";
    return `${clubName} ${sportLabel}${genderSuffix}${codeSuffix} takımı`;
  };

  const handleSelect = (team: TeamModel) => {
    const label = buildTeamLabel(team);
    const teamSlug = getTeamUrlSlug(team);
    // Format: (takim: label||id) — used by EntryRow to link to /takim/{tail},
    // the same one-segment shape the other four domains use. The backend
    // resolves the id and answers 301 with the canonical
    // /takim/{clubSlug}-{teamSlug}--{id}, which the page navigates to.
    // Any trailing "(...)" disambiguation suffix (see stripDisambiguationSuffix)
    // is stripped from the inline label; label is then lowercased (sözlük
    // convention — also turns teamCode "U19" into "u19"); the clubSlug/teamSlug
    // tail is untouched so navigation keeps working.
    onSelect(`(${applyRefMode("takim", refMode)}: ${toLowerTr(stripDisambiguationSuffix(label))}||${team.id})`);
    onClose();
  };

  const hasInput = query.trim().length >= 2;

  return (
    <div className="ref-popup" style={style}>
      <div className="ref-popup-header">
        <span className="ref-popup-title">{t("team_ref_search_title")}</span>
        <button type="button" className="ref-popup-close" onClick={onClose} title={t("close")}>✕</button>
      </div>
      {/* Insert mode switch — inline link vs "(bkz: ...)" note */}
      <RefModeToggle mode={refMode} onChange={setRefMode} />
      <div className="ref-popup-search-row">
        <input
          ref={inputRef}
          type="text"
          className="ref-popup-input"
          placeholder={t("team_ref_search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query !== "" && (
          <InputClearButton variant="inline" onClear={() => setQuery("")} />
        )}
      </div>
      <div className="ref-popup-results">
        {isLoading && <div className="ref-popup-status">{t("searching")}</div>}
        {!isLoading && error && (
          <div className="ref-popup-status ref-popup-status--error">{error}</div>
        )}
        {!isLoading && !error && hasInput && results.length === 0 && (
          <div className="ref-popup-status">{t("no_results")}</div>
        )}
        {!isLoading && results.map((team) => (
          <button
            key={team.id}
            type="button"
            className="ref-popup-result-item"
            onClick={() => handleSelect(team)}
          >
            {/* Primary: full team label; secondary: club abbreviation if available */}
            {buildTeamLabel(team)}
            {team.clubAbbreviation && (
              <span className="ref-popup-result-sub"> ({team.clubAbbreviation})</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── TopicRefSearchPopup ──────────────────────────────────────────────────────

interface TopicRefSearchPopupProps {
  onSelect: (text: string) => void;
  onClose: () => void;
  style?: React.CSSProperties;
  /** Pre-fills the search input with the text selected in the textarea when the button was clicked */
  initialQuery?: string;
}

/**
 * Popup for selecting a custom topic ("gündem") reference to insert as
 * (baslik: title||slug). The slug is used by EntryRow to link to /gundem/{slug}.
 * Reuses the public search category endpoint (customTopics) — only ACTIVE
 * topics are returned, consistent with the main search.
 */
export const TopicRefSearchPopup: React.FC<TopicRefSearchPopupProps> = ({ onSelect, onClose, style, initialQuery = "" }) => {
  const { t } = useTranslation();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<SearchCustomTopicResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  // Insert mode: inline link vs parenthesized "(bkz: ...)" note
  const [refMode, setRefMode] = useState<RefInsertMode>("inline");
  const inputRef = useRef<HTMLInputElement>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Focus the search input when the popup opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Close popup on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  // Debounced search — fires when query changes
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setError("");
      return;
    }
    debounceRef.current = setTimeout(async () => {
      setIsLoading(true);
      setError("");
      try {
        // customTopics category of the public search — returns id/title/slug
        const res = await searchCategory(trimmed, "customTopics", 0, SEARCH_MAX_RESULTS);
        setResults(res.customTopics?.content ?? []);
      } catch {
        setError(t("search_error"));
      } finally {
        setIsLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const handleSelect = (topic: SearchCustomTopicResult) => {
    // Format: (baslik: title||id) — tail used by EntryRow to link to
    // /gundem/{tail}. Storing only the id keeps the reference working if the
    // title, and therefore the slug, is corrected later.
    // Titles can carry a "(dizi)" / "(film)" style disambiguation suffix used
    // to tell same-named topics apart in search results — that suffix is
    // stripped from the inline label (see stripDisambiguationSuffix) so e.g.
    // "fer (dizi)" is inserted as just "fer"; the slug still points at the
    // exact disambiguated topic, so the link keeps working.
    // Titles are already stored lowercase, but lowercase defensively anyway;
    // the slug tail is untouched.
    onSelect(`(${applyRefMode("baslik", refMode)}: ${toLowerTr(stripDisambiguationSuffix(topic.title))}||${topic.id})`);
    onClose();
  };

  const hasInput = query.trim().length >= 2;

  return (
    <div className="ref-popup" style={style}>
      <div className="ref-popup-header">
        <span className="ref-popup-title">{t("topic_ref_search_title")}</span>
        <button type="button" className="ref-popup-close" onClick={onClose} title={t("close")}>✕</button>
      </div>
      {/* Insert mode switch — inline link vs "(bkz: ...)" note */}
      <RefModeToggle mode={refMode} onChange={setRefMode} />
      <div className="ref-popup-search-row">
        <input
          ref={inputRef}
          type="text"
          className="ref-popup-input"
          placeholder={t("topic_ref_search_placeholder")}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        {query !== "" && (
          <InputClearButton variant="inline" onClear={() => setQuery("")} />
        )}
      </div>
      <div className="ref-popup-results">
        {isLoading && <div className="ref-popup-status">{t("searching")}</div>}
        {!isLoading && error && (
          <div className="ref-popup-status ref-popup-status--error">{error}</div>
        )}
        {!isLoading && !error && hasInput && results.length === 0 && (
          <div className="ref-popup-status">{t("no_results")}</div>
        )}
        {!isLoading && results.map((topic) => (
          <button
            key={topic.id}
            type="button"
            className="ref-popup-result-item"
            onClick={() => handleSelect(topic)}
          >
            {/* Primary: topic title; secondary: category ("kanal") name if set */}
            {topic.title}
            {topic.categoryName && (
              <span className="ref-popup-result-sub"> ({topic.categoryName})</span>
            )}
          </button>
        ))}
      </div>
    </div>
  );
};

// ─── LinkPromptPopup ──────────────────────────────────────────────────────────

interface LinkPromptProps {
  selectedText: string;
  onInsert: (text: string) => void;
  onClose: () => void;
}

/**
 * Two-step popup for inserting a (link: url|display text) reference.
 * Step 1 — ask for the URL.
 * Step 2 — ask for the display text (pre-filled with any selected text).
 * Extracted from EntryPostForm so it can be reused in edit mode (EntryRow).
 */
export const LinkPromptPopup: React.FC<LinkPromptProps> = ({ selectedText, onInsert, onClose }) => {
  const { t } = useTranslation();
  const [step, setStep] = useState<1 | 2>(1);
  const [url, setUrl] = useState("");
  const [displayText, setDisplayText] = useState(selectedText);
  const inputRef = useRef<HTMLInputElement>(null);

  // Focus the input whenever the step changes
  useEffect(() => {
    inputRef.current?.focus();
  }, [step]);

  // Close on Escape
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  const handleStep1 = () => {
    const trimmed = url.trim();
    if (!trimmed) return;
    setStep(2);
  };

  const handleInsert = () => {
    const finalText = displayText.trim() || url.trim();
    // Display text is lowercased (sözlük convention); the URL itself is
    // preserved as typed — URL paths can be case-sensitive.
    onInsert(`(link: ${url.trim()}|${toLowerTr(finalText)})`);
    onClose();
  };

  return (
    <div className="link-popup">
      <div className="ref-popup-header">
        <span className="ref-popup-title">
          {step === 1 ? t("link_url_title") : t("link_display_title")}
        </span>
        <button type="button" className="ref-popup-close" onClick={onClose} title={t("close")}>✕</button>
      </div>

      {step === 1 ? (
        <>
          <input
            ref={inputRef}
            type="text"
            className="ref-popup-input"
            placeholder="https://..."
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleStep1()}
          />
          <div className="link-popup-actions">
            <button
              type="button"
              className="link-popup-btn"
              onClick={handleStep1}
              disabled={!url.trim()}
            >
              {t("next_step")}
            </button>
          </div>
        </>
      ) : (
        <>
          <input
            ref={inputRef}
            type="text"
            className="ref-popup-input"
            placeholder={t("link_display_placeholder")}
            value={displayText}
            onChange={(e) => setDisplayText(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && handleInsert()}
          />
          <div className="link-popup-actions">
            <button type="button" className="link-popup-btn link-popup-btn--secondary" onClick={() => setStep(1)}>
              {t("back_step")}
            </button>
            <button type="button" className="link-popup-btn" onClick={handleInsert}>
              {t("add_btn")}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

// ─── EntryEditorToolbar ───────────────────────────────────────────────────────

interface EntryEditorToolbarProps {
  /**
   * Ref to the input so cursor-based insertions work correctly.
   *
   * Typed as the three members this toolbar actually uses rather than as a
   * full HTMLTextAreaElement, so it accepts both a real <textarea> and the
   * ChipEditor, which exposes the same three mapped onto offsets in the
   * serialised text. Everything below therefore works unchanged for both.
   */
  textareaRef: React.RefObject<{
    selectionStart: number;
    selectionEnd: number;
    focus(): void;
  } | null>;
  content: string;
  setContent: (value: string) => void;
  /** When true, all toolbar buttons are disabled */
  disabled?: boolean;
  /**
   * When false, the görsel (image upload) button is hidden.
   * Pass false in ConversationView since image sharing in messages is not allowed.
   * Defaults to true (shown).
   */
  showImageButton?: boolean;
  /**
   * When false, the match reference and person reference buttons are hidden.
   * Pass false in ConversationView since match/person refs are not relevant in DMs.
   * Defaults to true (shown).
   */
  showRefButtons?: boolean;
  /**
   * When false, the alıntı (quote) button is hidden.
   * Pass false in ConversationView since MessageBubble does not render quote tokens.
   * Defaults to true (shown).
   */
  showQuoteButton?: boolean;
  /**
   * When false, the spoiler button is hidden.
   * Pass false in ConversationView since MessageBubble does not render spoiler tokens.
   * Defaults to true (shown).
   */
  showSpoilerButton?: boolean;
  /**
   * Notified whenever an image upload starts or finishes, so the parent form
   * can block submitting while a (gorsel: ...) token is still on its way.
   */
  onUploadingChange?: (uploading: boolean) => void;
}

/**
 * Shared toolbar row used in both EntryPostForm (new entry) and
 * EntryRow edit mode (existing entry). Renders ref, person ref, club ref,
 * team ref, bold, italic, link, and görsel buttons with their respective popup logic.
 *
 * The görsel button opens a hidden file input, uploads the selected image
 * to POST /api/media/entry-image, and inserts (gorsel: <url>) at the cursor.
 */
export const EntryEditorToolbar: React.FC<EntryEditorToolbarProps> = ({
  textareaRef,
  content,
  setContent,
  disabled = false,
  showImageButton = true,
  showRefButtons = true,
  showQuoteButton = true,
  showSpoilerButton = true,
  onUploadingChange,
}) => {
  const { t } = useTranslation();
  // Image upload is an author privilege: ROOKIE (çaylak) accounts do not get
  // the görsel button. The backend rejects the upload for them as well, this
  // only keeps the button out of sight.
  const { user } = useAuth();
  const canUploadImage = user?.role !== "ROOKIE";
  const refWrapperRef = useRef<HTMLDivElement>(null);
  const linkWrapperRef = useRef<HTMLDivElement>(null);
  // Single active popup state — only one popup can be open at a time (exclusive toggle)
  const [activePopup, setActivePopup] = useState<"ref" | "person" | "club" | "team" | "topic" | "link" | null>(null);
  // Selected text captured at the moment the link button is clicked
  const [capturedSelection, setCapturedSelection] = useState("");
  // Selected text captured for the ref popups — pre-fills their search input
  // Selection captured when the match ref button is pressed, so the popup
  // opens pre-filled the same way the other four ref popups do.
  const [capturedMatchQuery, setCapturedMatchQuery] = useState("");

  // Close active popup when the user clicks outside all popup wrappers
  useEffect(() => {
    if (!activePopup) return;
    const handleMouseDown = (e: MouseEvent) => {
      const wrappers = [
        refWrapperRef.current,
        linkWrapperRef.current,
      ];
      const target = e.target as Node;
      const insideWrapper = wrappers.some((w) => w?.contains(target));
      // Portalled popups are appended to body; check if the click is inside one of them
      const portalEls = Array.from(document.querySelectorAll(".ref-popup, .link-popup"));
      const insidePortal = portalEls.some((el) => el.contains(target));
      if (!insideWrapper && !insidePortal) {
        setActivePopup(null);
      }
    };
    document.addEventListener("mousedown", handleMouseDown);
    return () => document.removeEventListener("mousedown", handleMouseDown);
  }, [activePopup]);

  // Convenience derived booleans — kept so JSX below stays readable
  const refPopupOpen = activePopup === "ref";
  const linkPopupOpen = activePopup === "link";

  /**
   * On mobile (max-width 768px), calculate fixed position for popup so it
   * appears above the keyboard regardless of scroll or parent overflow.
   * Returns undefined on desktop — CSS position: absolute handles it there.
   */
  /**
   * On mobile, wrap popup in a portal so no parent CSS can affect it.
   * The wrapper div stops click propagation so the button's toggle
   * does not immediately close the popup after opening it.
   */
  const wrapWithPortal = (popup: React.ReactNode, wrapperRef: React.RefObject<HTMLDivElement | null>): React.ReactNode => {
    if (window.innerWidth > 768) return popup;

    // Position against the visual viewport, not the layout viewport. When the
    // on-screen keyboard is up these differ by the keyboard's height, and
    // measuring against the layout viewport is what pushed the popup down
    // behind the keyboard where its results could not be read or reached.
    const vv = window.visualViewport;
    const viewportTop = vv?.offsetTop ?? 0;
    const viewportHeight = vv?.height ?? window.innerHeight;

    const GAP = 4;
    const EDGE = 8;
    // Enough to show the mode toggle, the input, the filter row and a couple
    // of results. Below this the popup is not worth opening downward.
    const MIN_USABLE_HEIGHT = 220;

    const rect = wrapperRef.current?.getBoundingClientRect();

    let top: number;
    let maxHeight: number;

    if (!rect) {
      top = viewportTop + EDGE;
      maxHeight = viewportHeight - EDGE * 2;
    } else {
      const spaceBelow = viewportTop + viewportHeight - rect.bottom - GAP - EDGE;
      const spaceAbove = rect.top - viewportTop - GAP - EDGE;

      if (spaceBelow >= MIN_USABLE_HEIGHT || spaceBelow >= spaceAbove) {
        // Preferred: hangs off the button that opened it, so the connection
        // between the two is obvious.
        top = rect.bottom + GAP;
        maxHeight = Math.max(MIN_USABLE_HEIGHT, spaceBelow);
      } else {
        // Not enough room below — flip above the button rather than letting
        // the list run off the bottom of the screen.
        maxHeight = Math.max(MIN_USABLE_HEIGHT, spaceAbove);
        top = Math.max(viewportTop + EDGE, rect.top - GAP - maxHeight);
      }
      // Never start below the viewport, however the button was placed.
      top = Math.min(top, viewportTop + viewportHeight - MIN_USABLE_HEIGHT - EDGE);
      top = Math.max(top, viewportTop + EDGE);
    }

    return ReactDOM.createPortal(
      <div
        className="ref-popup-portal"
        style={{
          position: "fixed",
          top,
          left: EDGE,
          right: EDGE,
          // The popup scrolls inside this box instead of overflowing the
          // screen. Without it the result list simply extended past the
          // bottom edge with no way to reach the last few hits.
          maxHeight,
          zIndex: 9999,
        }}
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        {popup}
      </div>,
      document.body
    );
  };

  // Quote/spoiler modal open/close state — independent from activePopup since
  // the modals manage their own backdrop and outside-click behavior
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [showSpoilerModal, setShowSpoilerModal] = useState(false);

  // Image upload state
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [imageUploadError, setImageUploadError] = useState<string | null>(null);
  // Upload strip state — a local preview and a real byte-level percentage are
  // shown the moment a file is picked, so a slow connection no longer looks
  // like nothing happened at all.
  const [uploadPreview, setUploadPreview] = useState<string | null>(null);
  const [uploadFileName, setUploadFileName] = useState<string>("");
  const [uploadPercent, setUploadPercent] = useState<number>(0);
  const [uploadComputable, setUploadComputable] = useState<boolean>(true);
  const uploadAbortRef = useRef<AbortController | null>(null);

  // Keep the parent form in sync so it can disable its submit button
  useEffect(() => {
    onUploadingChange?.(isImageUploading);
  }, [isImageUploading, onUploadingChange]);
  const imageInputRef = useRef<HTMLInputElement>(null);

  /**
   * Insert text at the current cursor position, with optional space padding.
   */
  const insertAtCursor = (text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    let start = textarea.selectionStart;
    let end = textarea.selectionEnd;

    // Never break a word in half.
    //
    // A caret that sits between two letters is almost always a mis-tap rather
    // than an intention: nobody means to put a reference inside "boa" and end
    // up with "bo (ref) a". When nothing is selected and the caret has a word
    // character on both sides, the insertion point moves to the end of that
    // word, which is where the writer was aiming. A caret at a word boundary,
    // at a punctuation mark, or a real selection is left exactly where it is.
    const isWordChar = (ch: string): boolean => ch !== "" && !/[\s.,;:!?()[\]{}<>"'«»…]/.test(ch);
    if (start === end) {
      const charBefore = start > 0 ? content[start - 1] ?? "" : "";
      const charAfter = content[start] ?? "";
      if (isWordChar(charBefore) && isWordChar(charAfter)) {
        let i = start;
        while (i < content.length && isWordChar(content[i] ?? "")) i++;
        start = i;
        end = i;
      }
    }

    // No automatic space padding on either side.
    //
    // An earlier version padded the token with a space so the caret had an
    // ordinary text position to sit in after a chip. That is not worth the
    // cost: the writer sees gaps they did not type and cannot get rid of.
    // The token goes in exactly where the caret is, and the chip's own border
    // and padding are what separate it from the words around it.
    const insertion = text;
    const newContent = content.slice(0, start) + insertion + content.slice(end);
    setContent(newContent);
    requestAnimationFrame(() => {
      const pos = start + insertion.length;
      textarea.selectionStart = pos;
      textarea.selectionEnd = pos;
      textarea.focus();
    });
  };

  /**
   * Wrap the currently selected text (or cursor position) with a format tag.
   * If text is selected: replaces selection with (tag: selected text)
   * If nothing is selected: inserts (tag: ) with cursor inside
   */
  const wrapSelection = (tag: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = content.slice(start, end);
    // Wrap selected text or insert empty tag — no extra spaces, preserves word boundaries
    const wrapped = selected ? `(${tag}: ${selected})` : `(${tag}: )`;
    const newContent = content.slice(0, start) + wrapped + content.slice(end);
    setContent(newContent);
    requestAnimationFrame(() => {
      if (selected) {
        // Place cursor after the closing paren
        const pos = start + wrapped.length;
        textarea.selectionStart = pos;
        textarea.selectionEnd = pos;
      } else {
        // Place cursor inside the tag so user can start typing immediately
        const cursorPos = start + tag.length + 3; // "(tag: |)"
        textarea.selectionStart = cursorPos;
        textarea.selectionEnd = cursorPos;
      }
      textarea.focus();
    });
  };

  /**
   * Open the link popup, capturing any currently selected text first.
   */
  const handleLinkButton = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      setCapturedSelection(content.slice(textarea.selectionStart, textarea.selectionEnd));
    }
    setActivePopup("link");
  };

  /**
   * Open a ref popup (person/club/team), capturing any selected text to
   * pre-fill the popup's search input. Using onMouseDown with preventDefault
   * keeps the textarea selection alive when the button is clicked.
   */
  const captureSelectionAndOpen = (
    setter: React.Dispatch<React.SetStateAction<string>>,
    // Generic callback — caller decides which popup to open/close
    popupToggle: (toggle: boolean) => void
  ) => {
    const textarea = textareaRef.current;
    if (textarea) {
      setter(content.slice(textarea.selectionStart, textarea.selectionEnd).trim());
    }
    popupToggle(true);
  };

  /**
   * Insert the finished link reference into the textarea.
   */
  const handleLinkInsert = (text: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const hadSelection = end > start;

    if (hadSelection) {
      // Replace the selection with the link markup
      const prefix = start > 0 && content[start - 1] !== " " ? " " : "";
      const suffix = content[end] && content[end] !== " " ? " " : "";
      const newContent = content.slice(0, start) + prefix + text + suffix + content.slice(end);
      setContent(newContent);
    } else {
      insertAtCursor(text);
    }
  };

  /**
   * Insert a finished block token (alıntı/spoiler) into the textarea.
   * Placed on its own line — separated with single newlines only, never with
   * extra spaces, so the rendered block does not get stray blank lines.
   */
  const handleBlockInsert = (tokenText: string) => {
    const textarea = textareaRef.current;
    if (!textarea) {
      const joiner = content && !content.endsWith("\n") ? "\n" : "";
      setContent(content + joiner + tokenText);
      return;
    }
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const before = content.slice(0, start);
    const after = content.slice(end);
    const prefix = before && !before.endsWith("\n") ? "\n" : "";
    const suffix = after && !after.startsWith("\n") ? "\n" : "";
    const insertion = prefix + tokenText + suffix;
    setContent(before + insertion + after);
    requestAnimationFrame(() => {
      const pos = start + insertion.length;
      textarea.selectionStart = pos;
      textarea.selectionEnd = pos;
      textarea.focus();
    });
  };

  /**
   * Triggered when the user selects a file via the hidden image input.
   * Uploads to the server and inserts (gorsel: <opaque id>) at the cursor
   * position. Only the opaque id is written into the editor — never a server
   * path, a user id or an upload timestamp.
   *
   * A local object-URL preview and the progress bar appear immediately, before
   * a single byte has left the device — the upload itself may take a long time
   * on a mobile connection and the user has to see that it started.
   */
  const handleImageFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Reset so re-selecting the same file triggers onChange again
    e.target.value = "";

    const previewUrl = URL.createObjectURL(file);
    const controller = new AbortController();
    uploadAbortRef.current = controller;

    setImageUploadError(null);
    setUploadPreview(previewUrl);
    setUploadFileName(file.name);
    setUploadPercent(0);
    setUploadComputable(true);
    setIsImageUploading(true);

    try {
      const imageId = await uploadEntryImage(file, {
        signal: controller.signal,
        onProgress: ({ percent, computable }) => {
          setUploadPercent(percent);
          setUploadComputable(computable);
        },
      });
      // Start the token on its own line, except when it directly follows
      // another image token: consecutive images are meant to stay on the same
      // line so the previews are laid out side by side.
      const textarea = textareaRef.current;
      const before = textarea ? content.slice(0, textarea.selectionStart) : content;
      const afterImageToken =
        /\(gorsel:\s*(?:https?:\/\/[^)\s]+|[a-f0-9]{32}\.(?:jpg|jpeg|png|webp))\s*\)[ \t]*$/.test(before);
      const atLineStart = before.length === 0 || /\n[ \t]*$/.test(before);
      const prefix = afterImageToken || atLineStart ? "" : "\n";
      insertAtCursor(`${prefix}(gorsel: ${imageId})`);
    } catch (err: any) {
      const code: string = err?.message || "";
      // The user cancelled on purpose — that is not an error worth showing
      if (code !== "UPLOAD_ABORTED") {
        if (code === "INVALID_FILE_TYPE") {
          setImageUploadError(t("profile_upload_error_type"));
        } else if (code.startsWith("FILE_TOO_LARGE")) {
          setImageUploadError(t("toolbar_image_error_size"));
        } else if (code === "UPLOAD_NETWORK_ERROR") {
          setImageUploadError(t("toolbar_image_error_network"));
        } else {
          setImageUploadError(t("profile_upload_error_generic"));
        }
        // Auto-clear the error after 4 seconds
        setTimeout(() => setImageUploadError(null), 4000);
      }
    } finally {
      URL.revokeObjectURL(previewUrl);
      uploadAbortRef.current = null;
      setUploadPreview(null);
      setUploadFileName("");
      setUploadPercent(0);
      setIsImageUploading(false);
    }
  };

  /** Cancel an in-flight upload — aborts the request immediately. */
  const handleCancelUpload = () => {
    uploadAbortRef.current?.abort();
  };

  return (
    <div className="epf-toolbar-buttons">

      {/* Reference button — one picker for all five domains.
          Replaces the five separate buttons (gündem/maç/kulüp/takım/kişi)
          that used to sit here: the writer knows the name, not its category,
          so UnifiedRefPopup searches every domain at once and groups the
          results instead of asking which one to look in first.
          Hidden in ConversationView via showRefButtons=false. */}
      {showRefButtons && (
      <div ref={refWrapperRef} className="epf-bkz-wrapper">
        <button
          type="button"
          className={`epf-toolbar-btn epf-toolbar-btn--icon${refPopupOpen ? " epf-toolbar-btn--active" : ""}`}
          title={t("toolbar_reference_title")}
          onMouseDown={(e) => { e.preventDefault(); captureSelectionAndOpen(setCapturedMatchQuery, () => setActivePopup((prev) => prev === "ref" ? null : "ref")); }}
          disabled={disabled}
        >
          <FaHashtag size={14} />
        </button>
        {refPopupOpen && wrapWithPortal(
          <UnifiedRefPopup
            onSelect={insertAtCursor}
            onClose={() => setActivePopup(null)}
            initialQuery={capturedMatchQuery}
          />, refWrapperRef
        )}
      </div>
      )}

      {/* Bold button — wraps selection or inserts (b: ) */}
      <button
        type="button"
        className="epf-toolbar-btn"
        title={t("toolbar_bold_title")}
        onClick={() => wrapSelection("b")}
        disabled={disabled}
      >
        b
      </button>

      {/* Italic button — wraps selection or inserts (i: ) */}
      <button
        type="button"
        className="epf-toolbar-btn epf-toolbar-btn--italic"
        title={t("toolbar_italic_title")}
        onClick={() => wrapSelection("i")}
        disabled={disabled}
      >
        i
      </button>

      {/* alıntı — opens the quote modal; hidden in ConversationView via showQuoteButton=false */}
      {showQuoteButton && (
        <>
          <button
            type="button"
            className={`epf-toolbar-btn${showQuoteModal ? " epf-toolbar-btn--active" : ""}`}
            title={t("toolbar_quote_title")}
            onClick={() => setShowQuoteModal(true)}
            disabled={disabled}
          >
            alıntı
          </button>
          {showQuoteModal && (
            <TokenPromptModal
              titleKey="quote_modal_title"
              placeholderKey="quote_modal_placeholder"
              keyword="alinti"
              onInsert={handleBlockInsert}
              onClose={() => setShowQuoteModal(false)}
            />
          )}
        </>
      )}

      {/* spoiler — opens the spoiler modal, rendered as a collapsed block;
          hidden in ConversationView via showSpoilerButton=false */}
      {showSpoilerButton && (
        <>
          <button
            type="button"
            className={`epf-toolbar-btn${showSpoilerModal ? " epf-toolbar-btn--active" : ""}`}
            title={t("toolbar_spoiler_title")}
            onClick={() => setShowSpoilerModal(true)}
            disabled={disabled}
          >
            spoiler
          </button>
          {showSpoilerModal && (
            <TokenPromptModal
              titleKey="spoiler_modal_title"
              placeholderKey="spoiler_modal_placeholder"
              keyword="spoiler"
              onInsert={handleBlockInsert}
              onClose={() => setShowSpoilerModal(false)}
            />
          )}
        </>
      )}

      {/* Link button — opens two-step URL + display text popup */}
      <div ref={linkWrapperRef} className="epf-bkz-wrapper">
        <button
          type="button"
          className={`epf-toolbar-btn${linkPopupOpen ? " epf-toolbar-btn--active" : ""}`}
          title={t("toolbar_link_title")}
          onClick={handleLinkButton}
          disabled={disabled}
        >
          bağlantı
        </button>
        {linkPopupOpen && wrapWithPortal(
          <LinkPromptPopup
            selectedText={capturedSelection}
            onInsert={handleLinkInsert}
            onClose={() => setActivePopup(null)}
          />, linkWrapperRef
        )}
      </div>

      {/* görsel — image upload button; hidden in ConversationView via
          showImageButton=false, and hidden for ROOKIE users entirely */}
      {showImageButton && canUploadImage && (
        <>
          <button
            type="button"
            className={`epf-toolbar-btn${isImageUploading ? " epf-toolbar-btn--uploading" : ""}`}
            title={isImageUploading ? t("toolbar_image_uploading") : t("toolbar_image_title")}
            onClick={() => !isImageUploading && imageInputRef.current?.click()}
            disabled={disabled || isImageUploading}
          >
            {isImageUploading ? "..." : "görsel"}
          </button>

          {/* Hidden file input — only accepts browser-safe image formats */}
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            hidden
            onChange={handleImageFileSelect}
          />

          {/* Upload strip — full-width row under the buttons. Visible from the
              instant a file is picked, so the user always sees that something
              is happening even when the transfer takes half a minute. */}
          {isImageUploading && (
            <div className="epf-upload-strip">
              {uploadPreview && (
                <img
                  className="epf-upload-strip__thumb"
                  src={uploadPreview}
                  alt=""
                />
              )}
              <div className="epf-upload-strip__body">
                <div className="epf-upload-strip__top">
                  <span className="epf-upload-strip__name" title={uploadFileName}>
                    {uploadFileName}
                  </span>
                  <span className="epf-upload-strip__percent">
                    {uploadComputable
                      ? `%${uploadPercent}`
                      : t("toolbar_image_uploading")}
                  </span>
                </div>
                <div className="epf-upload-strip__track">
                  <div
                    className={`epf-upload-strip__bar${uploadComputable ? "" : " epf-upload-strip__bar--indeterminate"}`}
                    style={uploadComputable ? { width: `${uploadPercent}%` } : undefined}
                  />
                </div>
              </div>
              <button
                type="button"
                className="epf-upload-strip__cancel"
                onClick={handleCancelUpload}
                title={t("toolbar_image_cancel")}
                aria-label={t("toolbar_image_cancel")}
              >
                ×
              </button>
            </div>
          )}

          {/* Inline upload error — floats below the toolbar, auto-dismissed after 4s */}
          {imageUploadError && (
            <span className="epf-image-upload-error">{imageUploadError}</span>
          )}
        </>
      )}

    </div>
  );
};